
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api';
import { SearchableSelect } from './QuestionFilters';

export default function QuestionFormScreen({ token, setError }) {
  const { questionId } = useParams();
  const isEdit = !!questionId;
  const navigate = useNavigate();

  const [question, setQuestion] = useState({
    text: '',
    test_id: '',
    context_id: '',
    subject_id: '',
    topic_id: '',
    difficulty: 'Medium',
    options: [{ text: '', is_correct: false }],
  });
  const [subjects, setSubjects] = useState([]);
  const [tests, setTests] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef();
  const [loading, setLoading] = useState(true);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const fetchTopics = async (subjectId) => {
    if (!subjectId) {
      setTopics([]);
      return;
    }
    try {
      const res = await api.get(`/admin/subjects/${subjectId}/topics`, authHeaders());
      setTopics(res.data.topics || []);
    } catch (e) {
      setTopics([]);
    }
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const [sres, cres, tres] = await Promise.all([
          api.get('/admin/subjects', authHeaders()),
          api.get('/admin/contexts', authHeaders()),
          api.get('/admin/tests', authHeaders()),
        ]);
        setSubjects(sres.data.subjects || []);
        setContexts(cres.data.contexts || []);
        setTests(tres.data.tests || []);

        if (isEdit) {
          const questionResp = await api.get(`/admin/questions/${questionId}`, authHeaders());
          const q = questionResp.data.question;
          setQuestion({
            text: q.text || '',
            test_id: q.test_id || '',
            context_id: q.context_id || '',
            subject_id: q.subject_id || '',
            topic_id: q.topic_id || '',
            difficulty: q.difficulty || 'Medium',
            options: q.options ? q.options.map(o => ({ text: o.text, is_correct: o.is_correct })) : [{ text: '', is_correct: false }],
          });
          if (q.subject_id) {
            await fetchTopics(q.subject_id);
          }
        }
      } catch (e) {
        console.error('Failed to load questions data:', e);
        const errorMsg = e.response?.data?.error || e.message || 'Failed to load data';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [token, questionId, isEdit, setError]);

  const hasOneCorrect = question.options.filter(o => o.is_correct).length === 1;
  const valid = question.text.trim().length > 0 && hasOneCorrect && question.subject_id;

  const submitForm = async (e) => {
    e.preventDefault();
    if (!valid) return;

    try {
      const payload = {
        text: question.text,
        test_id: question.test_id || null,
        context_id: question.context_id || null,
        subject_id: question.subject_id || null,
        topic_id: question.topic_id || null,
        difficulty: question.difficulty,
        options: question.options,
      };

      let savedId;
      if (isEdit) {
        await api.put(`/admin/questions/${questionId}`, payload, authHeaders());
        savedId = questionId;
      } else {
        const resp = await api.post('/admin/questions', payload, authHeaders());
        savedId = resp.data.id;
      }

      if (imageFiles.length && savedId) {
        const form = new FormData();
        imageFiles.forEach((file) => form.append('images', file));
        await api.post(`/admin/questions/${savedId}/images`, form, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      navigate('/questions');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save question');
    }
  };

  const updateOption = (index, value) => {
    setQuestion(prev => ({
      ...prev,
      options: prev.options.map((o, i) => i === index ? { ...o, text: value } : o),
    }));
  };

  const toggleCorrect = (index) => {
    setQuestion(prev => ({
      ...prev,
      options: prev.options.map((o, i) => ({ ...o, is_correct: i === index })),
    }));
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold text-indigo-700 mb-4">{isEdit ? 'Edit Question' : 'Add New Question'}</h2>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <form className="space-y-6" onSubmit={submitForm}>
          {/* Section: Question Text & Test */}
          <div>
            <div className="mb-2 text-lg font-semibold text-slate-700">Question Details</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <input
                value={question.text}
                onChange={(e) => setQuestion((prev) => ({ ...prev, text: e.target.value }))}
                placeholder="Question text"
                className={`border rounded-lg p-2 focus:ring-2 focus:ring-indigo-200 ${!question.text.trim() ? 'border-red-300' : 'border-gray-300'}`}
                aria-label="Question text"
                required
              />
              <SearchableSelect
                options={tests.map((t) => ({ value: t.id, label: t.title }))}
                value={question.test_id}
                onChange={(v) => setQuestion((prev) => ({ ...prev, test_id: v }))}
                placeholder="(Optional) Test"
                aria-label="Select test"
              />
            </div>
            {!question.text.trim() && <div className="text-xs text-red-500 mt-1">Question text is required.</div>}
          </div>

          {/* Section: Subject, Topic, Context, Difficulty */}
          <div>
            <div className="mb-2 text-lg font-semibold text-slate-700">Classification</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <SearchableSelect
                options={subjects.map((s) => ({ value: s.id, label: s.name }))}
                value={question.subject_id}
                onChange={async (v) => {
                  setQuestion((prev) => ({ ...prev, subject_id: v, topic_id: '' }));
                  await fetchTopics(v);
                }}
                placeholder="Select subject"
                aria-label="Select subject"
              />
              <SearchableSelect
                options={topics.map((t) => ({ value: t.id, label: t.name }))}
                value={question.topic_id}
                onChange={(v) => setQuestion((prev) => ({ ...prev, topic_id: v }))}
                placeholder="Select topic"
                aria-label="Select topic"
                disabled={topics.length === 0}
              />
              <SearchableSelect
                options={contexts.map((c) => ({ value: c.id, label: c.title }))}
                value={question.context_id}
                onChange={(v) => setQuestion((prev) => ({ ...prev, context_id: v }))}
                placeholder="Select context"
                aria-label="Select context"
              />
              <SearchableSelect
                options={[
                  { value: 'Easy', label: 'Easy' },
                  { value: 'Medium', label: 'Medium' },
                  { value: 'Hard', label: 'Hard' },
                  { value: 'Very Hard', label: 'Very Hard' },
                ]}
                value={question.difficulty}
                onChange={(v) => setQuestion((prev) => ({ ...prev, difficulty: v }))}
                placeholder="Difficulty"
                aria-label="Select difficulty"
              />
            </div>
            {!question.subject_id && <div className="text-xs text-red-500 mt-1">Subject is required.</div>}
          </div>

          {/* Section: Options */}
          <div>
            <div className="mb-2 text-lg font-semibold text-slate-700">Options</div>
            <div className="space-y-2">
              {question.options.map((opt, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={opt.text}
                    onChange={(e) => updateOption(idx, e.target.value)}
                    className={`flex-1 border rounded-lg p-2 ${!opt.text.trim() ? 'border-red-300' : 'border-gray-300'}`}
                    placeholder={`Option ${idx + 1}`}
                    aria-label={`Option ${idx + 1}`}
                    required
                  />
                  <label className="inline-flex items-center gap-1 text-sm text-gray-600">
                    <input
                      type="radio"
                      name="correctOption"
                      checked={opt.is_correct}
                      onChange={() => toggleCorrect(idx)}
                      aria-label={`Mark option ${idx + 1} as correct`}
                    />
                    Correct
                  </label>
                </div>
              ))}
              {!hasOneCorrect && <div className="text-xs text-red-500">Exactly one option must be marked correct.</div>}
              <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => {
                  if (question.options.length < 5) {
                    setQuestion((prev) => ({
                      ...prev,
                      options: [...prev.options, { text: '', is_correct: false }],
                    }));
                  }
                }}
                aria-label="Add option"
              >
                + Add option
              </button>
            </div>
          </div>

          {/* Section: Images */}
          <div>
            <div className="mb-2 text-lg font-semibold text-slate-700">Images (optional)</div>
            <div
              className={`border-2 rounded-lg p-4 flex flex-col items-center gap-2 ${dragActive ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-white'}`}
              onDragOver={e => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
              onDrop={e => {
                e.preventDefault();
                setDragActive(false);
                const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith('image/'));
                setImageFiles(files);
              }}
              tabIndex={0}
              aria-label="Image upload area"
              onClick={() => fileInputRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => setImageFiles(Array.from(e.target.files || []))}
              />
              <span className="text-gray-500">Drag & drop images here, or click to select</span>
              {imageFiles.length > 0 && (
                <div className="flex gap-2 flex-wrap mt-2">
                  {imageFiles.map((f, i) => (
                    <div key={i} className="relative group">
                      <img
                        src={URL.createObjectURL(f)}
                        alt="preview"
                        className="h-20 rounded border border-gray-300"
                      />
                      <button
                        type="button"
                        className="absolute top-0 right-0 bg-white bg-opacity-80 rounded-full p-1 text-red-500 hover:text-red-700 shadow group-hover:visible invisible"
                        onClick={e => {
                          e.stopPropagation();
                          setImageFiles(files => files.filter((_, idx) => idx !== i));
                        }}
                        aria-label="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Section: Actions */}
          <div className="flex justify-between mt-6">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              onClick={() => navigate('/questions')}
              aria-label="Cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:bg-gray-400 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
              disabled={!valid}
              aria-label={isEdit ? 'Save Question' : 'Create Question'}
            >
              {isEdit ? 'Save Question' : 'Create Question'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
